import { useEffect, useState, useCallback } from "react";
import { Vault, SignOut, ArrowsLeftRight, CalendarCheck, UsersThree, CheckCircle, Megaphone, X, PushPin } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { formatMoney, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const INCOME_CATS = ["Satış", "Hizmet", "Tahsilat", "Diğer Gelir"];
const EXPENSE_CATS = ["Kira", "Tedarik", "Elektrik/Su", "Pazarlama", "Lojistik", "Ödeme", "Diğer"];
const TABS = [
  { id: "transaction", label: "Gelir / Gider", icon: ArrowsLeftRight, perm: "transactions" },
  { id: "payment", label: "Ödeme / Tahsilat", icon: CalendarCheck, perm: "payments" },
  { id: "contact", label: "Cari Ekle", icon: UsersThree, perm: "contacts" },
];

export default function StaffPanel() {
  const { user, logout } = useAuth();
  const { currencies } = useCurrency();
  const navigate = useNavigate();
  const perms = user?.permissions || { transactions: true, payments: true, contacts: true };
  const tabs = TABS.filter((t) => perms[t.perm]);
  const [tab, setTab] = useState(tabs[0]?.id || "transaction");
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    if (tabs.length && !tabs.some((t) => t.id === tab)) setTab(tabs[0].id);
  }, [tabs, tab]);

  useEffect(() => {
    api.get("/staff/my-messages").then((r) => setMessages(r.data)).catch(() => {});
  }, []);

  const ackMsg = async (id) => {
    setMessages((m) => m.filter((x) => x.id !== id));
    try { await api.post(`/messages/${id}/ack`); } catch (e) {}
  };

  const [tx, setTx] = useState({ type: "income", amount: "", currency: "TRY", category: "Satış", description: "", date: "" });
  const [pay, setPay] = useState({ type: "receivable", amount: "", currency: "TRY", contact_name: "", due_date: "", description: "" });
  const [con, setCon] = useState({ name: "", type: "customer", currency: "TRY", balance: "", email: "", phone: "" });

  const load = useCallback(() => {
    api.get("/staff/my-entries").then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const handleLogout = async () => { await logout(); navigate("/login"); };

  const saveTx = async () => {
    if (!tx.amount || Number(tx.amount) <= 0) return toast.error("Geçerli tutar girin");
    setSaving(true);
    try {
      await api.post("/transactions", { ...tx, amount: Number(tx.amount), account_id: null, date: tx.date ? new Date(tx.date).toISOString() : null });
      toast.success("İşlem kaydedildi");
      setTx({ type: "income", amount: "", currency: "TRY", category: "Satış", description: "", date: "" });
      load();
    } catch (e) { toast.error("Kaydedilemedi"); } finally { setSaving(false); }
  };

  const savePay = async () => {
    if (!pay.amount || !pay.due_date) return toast.error("Tutar ve vade tarihi girin");
    setSaving(true);
    try {
      await api.post("/payments", { ...pay, amount: Number(pay.amount), due_date: new Date(pay.due_date).toISOString() });
      toast.success("Kayıt eklendi");
      setPay({ type: "receivable", amount: "", currency: "TRY", contact_name: "", due_date: "", description: "" });
      load();
    } catch (e) { toast.error("Kaydedilemedi"); } finally { setSaving(false); }
  };

  const saveCon = async () => {
    if (!con.name) return toast.error("İsim girin");
    setSaving(true);
    try {
      await api.post("/contacts", { ...con, balance: Number(con.balance) || 0 });
      toast.success("Cari eklendi");
      setCon({ name: "", type: "customer", currency: "TRY", balance: "", email: "", phone: "" });
      load();
    } catch (e) { toast.error("Kaydedilemedi"); } finally { setSaving(false); }
  };

  const setT = (k, v) => setTx((f) => ({ ...f, [k]: v }));
  const setP = (k, v) => setPay((f) => ({ ...f, [k]: v }));
  const setC = (k, v) => setCon((f) => ({ ...f, [k]: v }));

  const recent = data ? [
    ...data.transactions.map((x) => ({ ...x, _kind: "İşlem", _label: `${x.type === "income" ? "Gelir" : "Gider"} · ${x.category}`, _date: x.date })),
    ...data.payments.map((x) => ({ ...x, _kind: "Ödeme/Tahsilat", _label: x.type === "receivable" ? "Tahsilat" : "Ödeme", _date: x.due_date })),
    ...data.contacts.map((x) => ({ ...x, _kind: "Cari", _label: x.name, _date: x.created_at })),
  ].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")).slice(0, 12) : [];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="h-16 border-b border-border bg-card/40 backdrop-blur-xl sticky top-0 z-20 flex items-center justify-between px-4 md:px-8">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-md bg-primary flex items-center justify-center">
            <Vault size={20} weight="fill" className="text-primary-foreground" />
          </div>
          <div>
            <div className="font-heading font-semibold tracking-tight leading-none">BEYT KASA</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground mt-0.5">Personel Girişi</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right hidden sm:block">
            <div className="text-sm">{user?.name}</div>
            <div className="text-xs text-muted-foreground">Personel</div>
          </div>
          <button onClick={handleLogout} data-testid="staff-logout-btn" className="text-muted-foreground hover:text-destructive transition-colors p-2" title="Çıkış">
            <SignOut size={18} />
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 md:p-8 space-y-6 animate-fade-in-up">
        <div>
          <h1 className="font-heading text-3xl font-light tracking-tight">Merhaba {user?.name?.split(" ")[0]} 👋</h1>
          <p className="text-sm text-muted-foreground mt-1">Yeni kayıt girin. Girdiğiniz kayıtlar şirket sistemine işlenir.</p>
        </div>

        {data?.kasa && (
          <div data-testid="staff-kasa-banner" className="flex items-center justify-between border border-primary/40 bg-primary/10 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-md bg-primary/20 flex items-center justify-center shrink-0">
                <Vault size={20} weight="fill" className="text-primary" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Kasanız</div>
                <div className="font-heading font-semibold">{data.kasa.name}</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Güncel Bakiye</div>
              <div className="font-heading text-xl font-semibold font-mono-num text-primary">{formatMoney(data.kasa.balance, data.kasa.currency, currencies)}</div>
            </div>
          </div>
        )}

        {messages.length > 0 && (
          <div className="space-y-2" data-testid="staff-messages">
            {messages.map((m) => m.pinned ? (
              <div key={m.id} data-testid={`staff-pinned-banner-${m.id}`} className="flex items-start gap-3 border border-amber-500/50 bg-amber-500/10 rounded-lg p-4">
                <PushPin size={20} weight="fill" className="text-amber-400 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wide text-amber-400 font-semibold mb-0.5">Sabit Uyarı</div>
                  <div className="text-sm text-foreground">{m.text}</div>
                  <div className="text-xs text-muted-foreground mt-1">{m.created_by_name}</div>
                </div>
              </div>
            ) : (
              <div key={m.id} data-testid={`staff-msg-banner-${m.id}`} className="flex items-start gap-3 border border-primary/40 bg-primary/10 rounded-lg p-4">
                <Megaphone size={20} weight="fill" className="text-primary mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-foreground">{m.text}</div>
                  <div className="text-xs text-muted-foreground mt-1">{m.created_by_name}</div>
                </div>
                <button onClick={() => ackMsg(m.id)} data-testid={`staff-msg-dismiss-${m.id}`} className="text-muted-foreground hover:text-foreground transition-colors" title="Kapat">
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        )}

        {data && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="staff-summary">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Bugün</div>
              <div className="font-heading text-2xl font-semibold text-primary font-mono-num">{data.summary.today_entries}</div>
              <div className="text-[10px] text-muted-foreground">giriş</div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Toplam İşlem</div>
              <div className="font-heading text-2xl font-semibold font-mono-num">{data.summary.total_transactions}</div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Ödeme/Tahsilat</div>
              <div className="font-heading text-2xl font-semibold font-mono-num">{data.summary.total_payments}</div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Cari</div>
              <div className="font-heading text-2xl font-semibold font-mono-num">{data.summary.total_contacts}</div>
            </div>
          </div>
        )}

        <div className="flex gap-2 flex-wrap">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`staff-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm transition-colors ${tab === t.id ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
              <t.icon size={18} /> {t.label}
            </button>
          ))}
          {tabs.length === 0 && <p className="text-sm text-muted-foreground">Henüz kayıt girme yetkiniz tanımlanmamış. Yöneticinize başvurun.</p>}
        </div>

        {tabs.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-6">
          {tab === "transaction" && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Tür</Label>
                  <Select value={tx.type} onValueChange={(v) => setT("type", v)}>
                    <SelectTrigger className="mt-1.5 bg-secondary border-border" data-testid="staff-tx-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="income">Gelir</SelectItem>
                      <SelectItem value="expense">Gider</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Kategori</Label>
                  <Select value={tx.category} onValueChange={(v) => setT("category", v)}>
                    <SelectTrigger className="mt-1.5 bg-secondary border-border" data-testid="staff-tx-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(tx.type === "income" ? INCOME_CATS : EXPENSE_CATS).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <Label className="text-xs uppercase text-muted-foreground">Tutar</Label>
                  <Input type="number" value={tx.amount} onChange={(e) => setT("amount", e.target.value)} className="mt-1.5 bg-secondary border-border" data-testid="staff-tx-amount" placeholder="0" />
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Birim</Label>
                  <Select value={tx.currency} onValueChange={(v) => setT("currency", v)}>
                    <SelectTrigger className="mt-1.5 bg-secondary border-border" data-testid="staff-tx-currency"><SelectValue /></SelectTrigger>
                    <SelectContent>{currencies.map((c) => <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Tarih</Label>
                  <Input type="date" value={tx.date} onChange={(e) => setT("date", e.target.value)} className="mt-1.5 bg-secondary border-border" data-testid="staff-tx-date" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Açıklama</Label>
                <Textarea value={tx.description} onChange={(e) => setT("description", e.target.value)} className="mt-1.5 bg-secondary border-border" rows={2} data-testid="staff-tx-description" />
              </div>
              <Button onClick={saveTx} disabled={saving} data-testid="staff-tx-save" className="bg-primary text-primary-foreground hover:bg-primary/90 w-full">Kaydet</Button>
            </div>
          )}

          {tab === "payment" && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Tür</Label>
                  <Select value={pay.type} onValueChange={(v) => setP("type", v)}>
                    <SelectTrigger className="mt-1.5 bg-secondary border-border" data-testid="staff-pay-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="receivable">Tahsilat (Alacak)</SelectItem>
                      <SelectItem value="payable">Ödeme (Borç)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Vade Tarihi</Label>
                  <Input type="date" value={pay.due_date} onChange={(e) => setP("due_date", e.target.value)} className="mt-1.5 bg-secondary border-border" data-testid="staff-pay-due" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <Label className="text-xs uppercase text-muted-foreground">Tutar</Label>
                  <Input type="number" value={pay.amount} onChange={(e) => setP("amount", e.target.value)} className="mt-1.5 bg-secondary border-border" data-testid="staff-pay-amount" placeholder="0" />
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Birim</Label>
                  <Select value={pay.currency} onValueChange={(v) => setP("currency", v)}>
                    <SelectTrigger className="mt-1.5 bg-secondary border-border" data-testid="staff-pay-currency"><SelectValue /></SelectTrigger>
                    <SelectContent>{currencies.map((c) => <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Taraf / Açıklama</Label>
                <Input value={pay.contact_name} onChange={(e) => setP("contact_name", e.target.value)} className="mt-1.5 bg-secondary border-border" data-testid="staff-pay-contact" placeholder="Firma / kişi adı" />
              </div>
              <div>
                <Label className="text-xs uppercase text-muted-foreground">Açıklama</Label>
                <Input value={pay.description} onChange={(e) => setP("description", e.target.value)} className="mt-1.5 bg-secondary border-border" data-testid="staff-pay-description" />
              </div>
              <Button onClick={savePay} disabled={saving} data-testid="staff-pay-save" className="bg-primary text-primary-foreground hover:bg-primary/90 w-full">Kaydet</Button>
            </div>
          )}

          {tab === "contact" && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Ünvan / İsim</Label>
                  <Input value={con.name} onChange={(e) => setC("name", e.target.value)} className="mt-1.5 bg-secondary border-border" data-testid="staff-con-name" />
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Tür</Label>
                  <Select value={con.type} onValueChange={(v) => setC("type", v)}>
                    <SelectTrigger className="mt-1.5 bg-secondary border-border" data-testid="staff-con-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="customer">Müşteri</SelectItem>
                      <SelectItem value="supplier">Tedarikçi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">E-posta</Label>
                  <Input value={con.email} onChange={(e) => setC("email", e.target.value)} className="mt-1.5 bg-secondary border-border" data-testid="staff-con-email" />
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Telefon</Label>
                  <Input value={con.phone} onChange={(e) => setC("phone", e.target.value)} className="mt-1.5 bg-secondary border-border" data-testid="staff-con-phone" />
                </div>
              </div>
              <Button onClick={saveCon} disabled={saving} data-testid="staff-con-save" className="bg-primary text-primary-foreground hover:bg-primary/90 w-full">Kaydet</Button>
            </div>
          )}
        </div>
        )}

        <div className="bg-card border border-border rounded-lg p-6">
          <h2 className="font-heading text-lg mb-4">Son Kayıtlarım</h2>
          <div className="space-y-2" data-testid="staff-recent">
            {recent.length === 0 ? (
              <p className="text-sm text-muted-foreground">Henüz kayıt girmediniz.</p>
            ) : recent.map((r, i) => (
              <div key={i} className="flex items-center gap-3 p-3 border border-border rounded-md bg-secondary/20">
                <CheckCircle size={18} className="text-[#10B981]" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{r._label}</div>
                  <div className="text-xs text-muted-foreground">{r._kind} · {formatDate(r._date)}</div>
                </div>
                {r.amount != null && <div className="font-mono-num text-sm">{formatMoney(r.amount, r.currency, currencies)}</div>}
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
